import React from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { DRAWER_Z_INDEX } from '../../../definitions/constants';
import { useI18n } from '../../../lib/hook/useI18n';
import FormValidatorHelper from '../../../Services/FormValidatorService';
import ThemableButton from '../../../theme/components/Button/ThemableButton';
import UnifiedConsentIdentityInfoNote from './UnifiedConsentIdentityInfoNote';
import PrivacyPolicyLink from '../../ConsentPromp/PrivacyPolicyLink';
import Analytics from '../../../tools/Analytics';
import { Events } from '../../../enums/Analytics';
import { UnifiedConsentType } from '../../../enums/UnifiedConsentType';

const useStyles = makeStyles((theme: Theme) => ({
  dialogTitle: {
    padding: 0,
    margin: 0,
  },
  headerBox: {
    padding: theme.spacing(3, 4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(3),
  },
  contentBox: {
    padding: theme.spacing(0, 4, 3),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
  },
  footer: {
    padding: theme.spacing(2, 4),
    height: 64,
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
  },
}));

interface Props {
  consentDeclinedSuppressionUi?: boolean;
  onCancel: () => void;
  open: boolean;
  saving: boolean;
  email: string;
  onEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e: React.FormEvent) => void;
  submitted: boolean;
}

const UnifiedContactIdentificationContent: React.FC<Props> = (
  props: Props
): JSX.Element => {
  const classes = useStyles();
  const { i18nClasses, t, tc } = useI18n();

  const {
    consentDeclinedSuppressionUi = false,
    onCancel,
    open,
    saving,
    email,
    onEmailChange,
    handleSubmit,
    submitted,
  } = props;

  const validator = new FormValidatorHelper();
  const validatorKey = t('room.contact.modal.email').toLowerCase();
  validator.check(validatorKey, email, 'required|email');
  const emailError = validator.getError(validatorKey, submitted);

  const handleCancel = () => {
    Analytics.track(Events.DSR_CHAT_CONSENT_CLOSED, {
      type: UnifiedConsentType.CONSENT_FROM_CHAT,
    });
    onCancel();
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    Analytics.track(Events.DSR_CONSENT_ACCEPTED, {
      type: UnifiedConsentType.CONSENT_FROM_CHAT,
    });
    handleSubmit(e);
  };

  const renderConsentText = () => (
    <Typography
      className={i18nClasses.root}
      color="textSecondary"
      variant="body2"
    >
      {tc(
        'room.welcome.modal.newer_consent.consent_with_note',
        { actionText: t('room.contact.modal.continue') },
        {
          privacyPolicyLink: (
            <PrivacyPolicyLink color="primary" variant="body2" />
          ),
        },
        true
      )}
    </Typography>
  );

  return (
    <Dialog
      BackdropProps={{
        style: {
          backdropFilter: 'blur(4px)',
          backgroundColor: 'var(--dialog-background-color-dark)',
        },
      }}
      fullWidth
      maxWidth="sm"
      open={open}
      sx={{ zIndex: DRAWER_Z_INDEX + 1 }}
    >
      <form onSubmit={handleFormSubmit} tabIndex={-1} noValidate>
        <Box className={classes.headerBox}>
          <DialogTitle className={classes.dialogTitle}>
            {t('room.contact.modal.title')}
          </DialogTitle>
        </Box>
        <Box className={classes.contentBox}>
          <TextField
            autoFocus
            color="secondary"
            error={!!emailError}
            fullWidth
            helperText={emailError}
            label={t('room.contact.modal.email')}
            onChange={onEmailChange}
            required
            type="email"
            value={email}
            variant="outlined"
          />
          {consentDeclinedSuppressionUi ? (
            <UnifiedConsentIdentityInfoNote
              actionText={t('room.contact.modal.continue')}
              showInfoIcon={false}
            />
          ) : (
            renderConsentText()
          )}
        </Box>
        <Box className={classes.footer}>
          <ThemableButton
            disabled={saving}
            onClick={handleCancel}
            variant="text"
          >
            {t('room.contact.modal.cancel')}
          </ThemableButton>
          <ThemableButton disabled={saving} type="submit" variant="contained">
            {t('room.contact.modal.continue')}
          </ThemableButton>
        </Box>
      </form>
    </Dialog>
  );
};

export default UnifiedContactIdentificationContent;
